import React, { Fragment, useMemo, useContext } from 'react';
import classNames from 'classnames';
import { Label, Link } from '@bingads-webui-react/fabric-control';
import { SurveyContext } from '@bingads-webui-campaign-react/survey-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SurveyContextType } from '../../../survey-context';
import { theme } from '../../../styles';
import { QuestionConfigurator } from '../../configurator/question';
import { ErrorString } from './common';
import { generateLabelId } from '../../../utility';

interface QuestionTitleProps {
  title: string,
  description: string,
  error: string,
  isRequired: boolean,
  isDirty: boolean,
  href: string | undefined,
  linkText: string | undefined,
  labelKey: string,
}

const Title = React.memo((props: QuestionTitleProps) => {
  const {
    title,
    isRequired,
    description,
    isDirty,
    error,
    href,
    linkText,
    labelKey,
  } = props;

  const classes: any = useDefaultStyles(() => ({
    requiredMark: {
      color: '#A80000',
    },
    questionTitle: {
      '& .ms-Label': {
        marginBottom: '8px',
        padding: 'unset',
      },
    },
    description: {
      fontSize: '12px',
      color: theme.palette.neutralSecondary,
    },
  }));

  const key = useMemo(() => generateLabelId(labelKey), [labelKey]);

  return (
    <div className={classNames(classes.questionTitle, 'question-title')}>
      <Label id={key}>
        {title}
        {isRequired && <span className={classNames(classes.requiredMark, 'required-mark')}>{' *'}</span>}
      </Label>
      {description &&
        <div className={classNames(classes.description, 'description')}>
          <span>{description}</span>
          {href && linkText &&
            <>
              <span>{' '}</span>
              <Link
                className="description-link"
                href={href}
                target="_blank"
              >{linkText}
              </Link>
            </>
          }
        </div>
      }
      {isDirty && error && <ErrorString error={error} />}
    </div>
  );
});

export const QuestionTitle = (props: {
  configurator: QuestionConfigurator,
  isDirty: boolean,
  error: string,
  children: React.ReactNode,
}) => {
  const {
    configurator,
    error,
    isDirty,
    children,
  } = props;

  const {
    isRequired,
    href,
    linkText,
    key,
  } = configurator.getConfiguration();

  // @ts-ignore
  const surveyContext: SurveyContextType = useContext(SurveyContext);

  const {
    getSurveyAnswers,
  } = surveyContext;

  const answers = getSurveyAnswers();

  const title = useMemo(() => configurator.getTitle(answers), [answers, configurator]);
  const description = useMemo(() => configurator.getDescription(answers), [answers, configurator]);

  return (
    <Fragment>
      <Title
        labelKey={key}
        isRequired={isRequired}
        description={description}
        title={title}
        isDirty={isDirty}
        href={href}
        linkText={linkText}
        error={error}
      />
      {children}
    </Fragment>
  );
};
